import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Center from '../../components/css/Center';
import Icon from '../../components/common/Icon';
import EventCta from '../../components/common/EventCta';
import { play } from '../../hooks/useSound';

const Page = () => {
  const [params] = useSearchParams();
  // App-level usePersistUrlParams strips ?token=... into sessionStorage on
  // mount, so by the time we read params here it may already be empty.
  // Try the URL first, then fall back to sessionStorage.
  const token = params.get('token') || sessionStorage.getItem('token') || '';

  const [state, setState] = useState('loading'); // 'loading' | 'confirmed' | 'already_counted' | 'expired' | 'vote_closed' | 'error' | 'no_token'
  const [team, setTeam] = useState({ id: null, name: null });

  // Guard against React StrictMode firing useEffect twice in dev — without this
  // the two near-simultaneous POSTs both pass the cheer_rating_exists check
  // before either commits, creating duplicate Cheer rows.
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setState('no_token');
      return;
    }

    if (firedRef.current) return;
    firedRef.current = true;

    let ignore = false;

    const run = async () => {
      try {
        const res = await fetch('/api/confirm-cheer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        let body = {};
        try { body = await res.json(); } catch { /* ignore */ }

        if (ignore) return;

        if (res.ok && body.message === 'voteConfirmed') {
          setTeam({ id: body.team_id, name: body.team_name, event_id: body.event_id, event_name: body.event_name, referral_link: body.referral_link });
          setState('confirmed');
          play('cheer');
        } else if (res.ok && body.message === 'voteAlreadyCounted') {
          setTeam({ id: body.team_id, name: body.team_name, event_id: body.event_id, event_name: body.event_name, referral_link: body.referral_link });
          setState('already_counted');
        } else if (body.message === 'invalidConfirmationToken' || body.message === 'voteNotFound') {
          setState('expired');
        } else if (body.message === 'voteClosed') {
          setState('vote_closed');
        } else {
          setState('error');
        }
      } catch {
        if (!ignore) setState('error');
      }
    };

    run();
    return () => { ignore = true; };
  }, [token]);

  const renderBody = () => {
    if (state === 'loading') {
      return (
        <Card p="3">
          <Title heading="5" my="1">Please wait...</Title>
        </Card>
      );
    }

    if (state === 'confirmed') {
      return (
        <Card p="3" className="text-center">
          <Center>
            <div style={{ fontSize: '3.5rem', color: 'var(--primary-color)' }}>
              <Icon name="check-circle" theme="primary" pack="sr" />
            </div>
          </Center>
          <Title heading="3" my="1">Your vote counts! 🎉</Title>
          <p className="text-secondary">
            {team.name ? `Thanks for voting for ${team.name}.` : 'Thanks for voting!'}
          </p>
          {team.id && (
            <Link to={`/public-team/${team.id}`}>
              <Button>Back to team</Button>
            </Link>
          )}
        </Card>
      );
    }

    if (state === 'already_counted') {
      return (
        <Card p="3" className="text-center">
          <Title heading="4" my="1">Already counted</Title>
          <p className="text-secondary">
            {team.name
              ? `You've already voted for ${team.name} — nothing more to do.`
              : "You've already voted for this team — nothing more to do."}
          </p>
          {team.id && (
            <Link to={`/public-team/${team.id}`}>
              <Button>Back to team</Button>
            </Link>
          )}
        </Card>
      );
    }

    if (state === 'expired') {
      return (
        <Card p="3" className="text-center">
          <Title heading="4" my="1">Link expired or invalid</Title>
          <p className="text-secondary">
            This confirmation link has expired or was already used. Submit a new vote to try again.
          </p>
        </Card>
      );
    }

    if (state === 'vote_closed') {
      return (
        <Card p="3" className="text-center">
          <Title heading="4" my="1">Voting is closed</Title>
          <p className="text-secondary">
            Voting is closed for this event. Your vote could not be counted.
          </p>
        </Card>
      );
    }

    if (state === 'no_token') {
      return (
        <Card p="3" className="text-center">
          <Title heading="4" my="1">No token provided</Title>
          <p className="text-secondary">
            Open the confirmation link from your email.
          </p>
        </Card>
      );
    }

    return (
      <Card p="3" className="text-center">
        <Title heading="4" my="1">Something went wrong</Title>
        <p className="text-secondary">
          We couldn't confirm your vote. Try again from the link in your email.
        </p>
      </Card>
    );
  };

  return (
    <Screen color="#f3f4f6" p="3">
      <div className="top">
        <div className="public-page-shell">
          {renderBody()}
          <EventCta eventName={team?.event_name} eventId={team?.event_id} referralLink={team?.referral_link} commercialUrl={team?.commercial_url} />
        </div>
      </div>
    </Screen>
  );
};

export default Page;
